import { useState, useEffect, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import useShare from '../../Share/components/hooks/useShare';
import useActivation from './hooks/useActivation';
import ShareList from './ShareList';
import { CSVLink } from 'react-csv';
import PageWithTable from '@/src/Common/components/PageWithTable';
import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import Loading from '@/src/Common/components/Loading';
import useSurveyResults from './hooks/useSurveyResults';

export default function ShareManager() {
    const { activationId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [activation, setActivation] = useState<ActivationObject | null>(null);
    const [shares, setShares] = useState<(ShareObject & MediaInstance)[]>([]);
    const [csvShares, setCsvShares] = useState<any[]>([]);
    const { getActivation } = useActivation();
    const { getSharesForActivation, reprocessUnsentShares } = useShare();
    const { loadSurveyResults, surveyResults } = useSurveyResults();

    const loadShares = async () => {
        try {
            const activation = await getActivation(
                activationId ? +activationId : 0,
            );

            // @ts-ignore
            setActivation(activation);

            const shares = await getSharesForActivation(activation.id);
            const typeLabels: Record<number, string> = {
                0: 'Email',
                1: 'Text',
                2: 'WhatsApp',
            };
            const csvShares = shares.map((share: any) => ({
                destination: share.destination,
                mediaUrl: share.originalUri,
                type: typeLabels[share.type] || share.type,
                disclaimerAccepted: share.disclaimerAccepted,
                createdAt: share.createdAt,
            }));

            setCsvShares(csvShares);
            setShares(shares);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadShares();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSurveyResults(activationId ? +activationId : 0);
    }, [activationId, loadSurveyResults]);

    const reprocessSharesAction = async () => {
        if (isReprocessing) {
            return;
        }
        setIsReprocessing(true);
        await reprocessUnsentShares(activationId ? +activationId : 0);
        setIsReprocessing(false);
    };

    if (isLoading) {
        return <Loading isLoading={true} />;
    }

    let actions = (
        <>
            {surveyResults.length > 0 && (
                <CSVLink
                    data={surveyResults}
                    filename={`${activation?.name}SurveyResults.csv`}
                    target="_blank"
                >
                    <span className=" pointer ml-auto flex items-center gap-x-1 rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black">
                        Download Survey Results ({surveyResults.length})
                    </span>
                </CSVLink>
            )}
            <CSVLink
                data={csvShares}
                filename={`${activation?.name}Shares.csv`}
                target="_blank"
            >
                <span className="pointer ml-auto flex items-center gap-x-1 rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black">
                    Download Shares ({csvShares.length})
                </span>
            </CSVLink>

            <Menu as="div" className="relative flex-none">
                <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                    <span className="sr-only">Open options</span>
                    <EllipsisVerticalIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                    />
                </Menu.Button>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                            {({ active }) => (
                                <span
                                    onClick={() => reprocessSharesAction()}
                                    className={classNames(
                                        active ? 'bg-gray-50' : '',
                                        'block px-3 py-1 text-sm leading-6 text-gray-900',
                                    )}
                                >
                                    {isReprocessing
                                        ? 'Reprocessing...'
                                        : 'Reprocess'}
                                </span>
                            )}
                        </Menu.Item>
                    </Menu.Items>
                </Transition>
            </Menu>
        </>
    );

    return (
        <PageWithTable
            title="Shares"
            subtitle={`Total ${shares.length}`}
            actions={actions}
        >
            <ShareList
                shares={shares}
                reloadShares={() => {
                    loadShares();
                }}
            />
        </PageWithTable>
    );
}

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}
